/**
 * Pure derivation of the 7-day daily-reward ladder for the Profile card (specs/profile-page).
 * Mirrors the day math the DailyRewardModal uses (getRealActiveStreak → currentDay), so both
 * surfaces agree. No hooks, no store access — the card feeds in the store values.
 */

import { DAILY_CLAIM_LADDER } from '../../../economy/constants/economy.constants';
import { getRealActiveStreak } from '../../../economy/helpers/dailyClaimStreakDisplay.helper';

export interface DailyLadderDay {
  day: number;
  amount: number;
  claimed: boolean;
  today: boolean;
  locked: boolean;
}

export interface DailyLadder {
  currentDay: number;
  canClaim: boolean;
  /** DAILY_CLAIM_LADDER[currentDay - 1]. */
  todayReward: number;
  /** How many of the 7 have been collected this cycle (for "N/7"). */
  collectedCount: number;
  /** True once all 7 in the current cycle are collected (day 7 claimed, awaiting reset). */
  weekComplete: boolean;
  days: DailyLadderDay[];
}

export function deriveDailyLadder(
  dailyClaimStreak: number,
  lastDailyClaimAt: string | null,
  nextClaimAt: string | null,
): DailyLadder {
  const activeStreak = getRealActiveStreak(dailyClaimStreak, lastDailyClaimAt);
  const currentDay = ((activeStreak - 1) % 7) + 1;
  const canClaim = nextClaimAt === null;

  const days: DailyLadderDay[] = DAILY_CLAIM_LADDER.map((amount, idx) => {
    const day = idx + 1;
    const claimed = day < currentDay || (day === currentDay && !canClaim);
    const today = day === currentDay && canClaim;
    return { day, amount, claimed, today, locked: !claimed && !today };
  });

  return {
    currentDay,
    canClaim,
    todayReward: DAILY_CLAIM_LADDER[currentDay - 1],
    collectedCount: canClaim ? currentDay - 1 : currentDay,
    weekComplete: !canClaim && currentDay === 7,
    days,
  };
}
